import { Injectable } from '@angular/core';
import Swal, { SweetAlertOptions } from 'sweetalert2';

/**
 * Centralized Message Service
 * Clean Minimal Light UI Style — Primary Blue #3B82F6
 * Manages application-wide notifications, alerts, and confirmations.
 */
@Injectable({
  providedIn: 'root'
})
export class MessageService {

  // Clean Minimal Palette
  private readonly COLORS = {
    primary: '#3B82F6',
    primaryHover: '#2563EB',
    danger: '#EF4444',
    success: '#10B981',
    warning: '#F59E0B',
    text: '#111827',
    textSecondary: '#6B7280',
    surface: '#F9FAFB',
    border: '#E5E7EB',
    white: '#FFFFFF'
  };

  constructor() {
    this.injectGlobalStyles();
  }

  /**
   * Inject global SweetAlert2 overrides for the Clean Minimal Light theme.
   */
  private injectGlobalStyles(): void {
    if (document.getElementById('swal-clean-minimal-styles')) return;

    const style = document.createElement('style');
    style.id = 'swal-clean-minimal-styles';
    style.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');

      /* ── Popup Container ── */
      .swal-clean-popup {
        font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif !important;
        border-radius: 20px !important;
        padding: 2em 1.8em 1.6em !important;
        box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 20px 60px rgba(0,0,0,0.08) !important;
        border: none !important;
        max-width: 420px !important;
      }

      /* ── Title ── */
      .swal-clean-popup .swal2-title {
        font-family: 'Outfit', sans-serif !important;
        font-weight: 700 !important;
        font-size: 1.25rem !important;
        color: #111827 !important;
        letter-spacing: -0.03em !important;
        padding-top: 0.4em !important;
      }

      /* ── Body Text ── */
      .swal-clean-popup .swal2-html-container,
      .swal-clean-popup .swal2-content {
        font-family: 'Outfit', sans-serif !important;
        font-size: 0.9rem !important;
        color: #6B7280 !important;
        line-height: 1.55 !important;
      }

      /* ── Icon Adjustments ── */
      .swal-clean-popup .swal2-icon {
        margin: 0.5em auto 0.8em !important;
        border-width: 2px !important;
        width: 52px !important;
        height: 52px !important;
      }
      .swal-clean-popup .swal2-icon .swal2-icon-content {
        font-size: 1.6rem !important;
      }
      .swal-clean-popup .swal2-icon.swal2-question {
        border-color: #DBEAFE !important;
        color: #3B82F6 !important;
        background: #EFF6FF !important;
      }
      .swal-clean-popup .swal2-icon.swal2-warning {
        border-color: #FEF3C7 !important;
        color: #F59E0B !important;
        background: #FFFBEB !important;
      }
      .swal-clean-popup .swal2-icon.swal2-error {
        border-color: #FEE2E2 !important;
        color: #EF4444 !important;
        background: #FEF2F2 !important;
      }
      .swal-clean-popup .swal2-icon.swal2-success {
        border-color: #D1FAE5 !important;
        color: #10B981 !important;
        background: #ECFDF5 !important;
      }
      .swal-clean-popup .swal2-icon.swal2-info {
        border-color: #DBEAFE !important;
        color: #3B82F6 !important;
        background: #EFF6FF !important;
      }
      .swal-clean-popup .swal2-icon.swal2-success [class^='swal2-success-line'] {
        background-color: #10B981 !important;
      }
      .swal-clean-popup .swal2-icon.swal2-success .swal2-success-ring {
        border-color: rgba(16,185,129,0.3) !important;
      }
      .swal-clean-popup .swal2-icon.swal2-error [class^='swal2-x-mark-line'] {
        background-color: #EF4444 !important;
        top: 50% !important;
        left: 50% !important;
        width: 60% !important;
        height: 3px !important;
        border-radius: 2px !important;
        display: block !important;
        position: absolute !important;
      }
      .swal-clean-popup .swal2-icon.swal2-error .swal2-x-mark-line-left {
        transform: translate(-50%, -50%) rotate(45deg) !important;
      }
      .swal-clean-popup .swal2-icon.swal2-error .swal2-x-mark-line-right {
        transform: translate(-50%, -50%) rotate(-45deg) !important;
      }
      .swal-clean-popup .swal2-icon.swal2-error .swal2-x-mark {
        position: relative !important;
        width: 100% !important;
        height: 100% !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
      }

      /* ── Buttons ── */
      .swal-clean-popup .swal2-actions {
        gap: 10px !important;
        margin-top: 1.2em !important;
      }

      .swal-clean-confirm {
        font-family: 'Outfit', sans-serif !important;
        font-weight: 600 !important;
        font-size: 0.9rem !important;
        border-radius: 10px !important;
        padding: 0.65em 1.6em !important;
        background: #222 !important;
        color: #fff !important;
        border: none !important;
        transition: all 0.15s ease !important;
        box-shadow: none !important;
        letter-spacing: -0.01em !important;
      }
      .swal-clean-confirm:hover {
        background: #111 !important;
      }
      .swal-clean-confirm:focus {
        box-shadow: 0 0 0 3px rgba(34,34,34,0.2) !important;
      }

      .swal-clean-confirm-primary {
        font-family: 'Outfit', sans-serif !important;
        font-weight: 600 !important;
        font-size: 0.9rem !important;
        border-radius: 10px !important;
        padding: 0.65em 1.6em !important;
        background: #3B82F6 !important;
        color: #fff !important;
        border: none !important;
        transition: all 0.15s ease !important;
        box-shadow: none !important;
      }
      .swal-clean-confirm-primary:hover {
        background: #2563EB !important;
      }
      .swal-clean-confirm-primary:focus {
        box-shadow: 0 0 0 3px rgba(59,130,246,0.25) !important;
      }

      .swal-clean-cancel {
        font-family: 'Outfit', sans-serif !important;
        font-weight: 600 !important;
        font-size: 0.9rem !important;
        border-radius: 10px !important;
        padding: 0.65em 1.6em !important;
        background: #fff !important;
        color: #6B7280 !important;
        border: 1.5px solid #E5E7EB !important;
        transition: all 0.15s ease !important;
        box-shadow: none !important;
      }
      .swal-clean-cancel:hover {
        background: #F9FAFB !important;
        border-color: #D1D5DB !important;
        color: #374151 !important;
      }
      .swal-clean-cancel:focus {
        box-shadow: 0 0 0 3px rgba(107,114,128,0.1) !important;
      }

      .swal-clean-confirm-danger {
        font-family: 'Outfit', sans-serif !important;
        font-weight: 600 !important;
        font-size: 0.9rem !important;
        border-radius: 10px !important;
        padding: 0.65em 1.6em !important;
        background: #EF4444 !important;
        color: #fff !important;
        border: none !important;
        transition: all 0.15s ease !important;
        box-shadow: none !important;
      }
      .swal-clean-confirm-danger:hover {
        background: #DC2626 !important;
      }
      .swal-clean-confirm-danger:focus {
        box-shadow: 0 0 0 3px rgba(239,68,68,0.25) !important;
      }

      /* ── Backdrop ── */
      .swal-clean-backdrop {
        background: rgba(17,24,39,0.15) !important;
        backdrop-filter: blur(4px) !important;
      }

      /* ── Toast ── */
      .swal-clean-toast {
        font-family: 'Outfit', sans-serif !important;
        border-radius: 12px !important;
        box-shadow: 0 4px 24px rgba(0,0,0,0.1) !important;
        padding: 0.8em 1.2em !important;
      }
      .swal-clean-toast .swal2-title {
        font-family: 'Outfit', sans-serif !important;
        font-weight: 600 !important;
        font-size: 0.88rem !important;
        color: #111827 !important;
      }

      /* ── Timer Progress Bar ── */
      .swal-clean-toast .swal2-timer-progress-bar {
        background: #3B82F6 !important;
        border-radius: 0 0 12px 12px !important;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Base configuration for the Clean Minimal Light theme.
   */
  private get baseConfig(): SweetAlertOptions {
    return {
      buttonsStyling: false,
      padding: '2em',
      color: this.COLORS.text,
      backdrop: true,
      customClass: {
        popup: 'swal-clean-popup',
        confirmButton: 'swal-clean-confirm',
        cancelButton: 'swal-clean-cancel'
      }
    };
  }

  /** Config with blue primary confirm button */
  private get primaryConfig(): SweetAlertOptions {
    return {
      ...this.baseConfig,
      customClass: {
        ...this.baseConfig.customClass as Record<string, string>,
        confirmButton: 'swal-clean-confirm-primary'
      }
    };
  }

  /** Config with red danger confirm button */
  private get dangerConfig(): SweetAlertOptions {
    return {
      ...this.baseConfig,
      customClass: {
        ...this.baseConfig.customClass as Record<string, string>,
        confirmButton: 'swal-clean-confirm-danger'
      }
    };
  }

  // ============================================================
  // RETRY & CONNECTION ERRORS
  // ============================================================

  showRetryError(): Promise<any> {
    return Swal.fire({
      ...this.primaryConfig,
      icon: 'warning',
      title: 'Request Failed',
      html: `
        <div style="line-height: 1.55;">
          Unable to complete your request after 3 attempts.<br><br>
          <strong style="color: ${this.COLORS.text};">Your chat session is still active</strong> - please try again.
        </div>
      `,
      confirmButtonText: 'Continue Chatting'
    });
  }

  showUnexpectedError(): Promise<any> {
    return Swal.fire({
      ...this.baseConfig,
      icon: 'error',
      title: 'Chat Issue',
      text: 'An unexpected error occurred. Please try again in a few minutes.',
      confirmButtonText: 'OK'
    });
  }

  showConnectionIssue(): Promise<any> {
    return Swal.fire({
      ...this.primaryConfig,
      icon: 'warning',
      title: 'Connection Issue',
      html: `
        <div style="line-height: 1.55;">
          Unable to complete your request after 3 attempts. 
          <strong style="color: ${this.COLORS.text};">Your chat session is still active</strong> - please try again in a few minutes.
        </div>
      `,
      confirmButtonText: 'OK'
    });
  }

  // ============================================================
  // TOKEN LIMIT ERRORS
  // ============================================================

  showTokenLimitExceeded(params: {
    model: string;
    tokensNeeded: number;
    tokensRemaining: number;
    tokensShort: number;
    estimatedCharactersToReduce: number;
    isEstimated?: boolean;
  }): Promise<any> {
    const tokensLabel = params.isEstimated ? 'Tokens needed (estimated)' : 'Tokens needed';

    const statsHtml = `
      <div style="background: ${this.COLORS.surface}; padding: 14px 16px; border-radius: 10px; margin: 14px 0; font-size: 0.88em; border: 1px solid ${this.COLORS.border};">
        <div style="display:flex; justify-content:space-between; margin-bottom:6px; color: ${this.COLORS.textSecondary};">
          <span>Model</span>
          <strong style="color: ${this.COLORS.text};">${params.model.replace(/-/g, ' ')}</strong>
        </div>
        <div style="display:flex; justify-content:space-between; margin-bottom:6px; color: ${this.COLORS.danger};">
          <span>${tokensLabel}</span>
          <strong>${params.tokensNeeded.toLocaleString()}</strong>
        </div>
        <div style="display:flex; justify-content:space-between; margin-bottom:6px; color: ${this.COLORS.textSecondary};">
          <span>Tokens remaining</span>
          <strong style="color: ${this.COLORS.text};">${params.tokensRemaining.toLocaleString()}</strong>
        </div>
        <div style="display:flex; justify-content:space-between; border-top: 1px solid ${this.COLORS.border}; padding-top:8px; margin-top:4px; color: ${this.COLORS.danger};">
          <span>Tokens short</span>
          <strong>${params.tokensShort.toLocaleString()}</strong>
        </div>
      </div>
      <p style="font-size: 0.9em; color: ${this.COLORS.textSecondary}; line-height: 1.5;">
        Please reduce your prompt by approximately <strong style="color:${this.COLORS.danger}">${params.estimatedCharactersToReduce}</strong> characters or start a new conversation.
      </p>
    `;

    return Swal.fire({
      ...this.baseConfig,
      icon: 'warning',
      title: 'Token Limit Exceeded',
      html: statsHtml,
      confirmButtonText: 'OK'
    });
  }

  // ============================================================
  // FILE VALIDATION ERRORS
  // ============================================================

  showInvalidFileTypes(invalidFiles: string[]): Promise<any> {
    return Swal.fire({
      ...this.baseConfig,
      icon: 'error',
      title: 'Invalid File(s)',
      html: `
        <p style="color: ${this.COLORS.textSecondary}; margin-bottom: 10px;">The following file types are not supported:</p>
        <div style="text-align:left; background:#FEF2F2; color:#991B1B; padding:12px 14px; border-radius:10px; border: 1px solid #FECACA; font-size: 0.88em; line-height: 1.6;">
          ${invalidFiles.map(f => `• ${f}`).join('<br>')}
        </div>
      `,
      confirmButtonText: 'OK'
    });
  }

  showFileSizeExceeded(model: string, maxSizeMB: number): Promise<any> {
    return Swal.fire({
      ...this.baseConfig,
      icon: 'warning',
      title: 'File Size Limit Exceeded',
      html: `
        <div style="color: ${this.COLORS.textSecondary}; line-height: 1.55;">
          Total file size exceeds the allowed limit for:<br>
          <strong style="color: ${this.COLORS.text};">${model.replace(/-/g, ' ')}</strong>
          <br><br><strong style="color: ${this.COLORS.text};">Max allowed:</strong> ${maxSizeMB} MB
        </div>
      `,
      confirmButtonText: 'OK'
    });
  }

  showUnsupportedFileType(): Promise<any> {
    return Swal.fire({
      ...this.baseConfig,
      icon: 'error',
      title: 'Unsupported File Type',
      text: 'Please upload a supported file format.',
      confirmButtonText: 'OK'
    });
  }

  // ============================================================
  // SESSION MANAGEMENT
  // ============================================================

  showDeleteSessionConfirmation(): Promise<any> {
    return Swal.fire({
      ...this.baseConfig,
      title: 'Delete Chat?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      reverseButtons: true,
      focusCancel: true,
      customClass: {
        ...this.baseConfig.customClass as Record<string, string>,
        confirmButton: 'swal-clean-confirm-danger',
        cancelButton: 'swal-clean-cancel'
      },
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel'
    });
  }

  showSessionDeleted(sessionTitle: string): Promise<any> {
    return Swal.fire({
      icon: 'success',
      title: 'Deleted!',
      text: `"${sessionTitle}" has been removed.`,
      toast: true,
      position: 'bottom-end',
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
      customClass: {
        popup: 'swal-clean-toast'
      }
    });
  }

  showSessionPinned(isPinned: boolean): Promise<any> {
    const Toast = Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 1500,
      timerProgressBar: false,
      customClass: {
        popup: 'swal-clean-toast'
      },
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer);
        toast.addEventListener('mouseleave', Swal.resumeTimer);
      }
    });

    return Toast.fire({
      icon: 'success',
      title: isPinned ? 'Chat Pinned!' : 'Chat Unpinned!'
    });
  }

  showSessionDeleteError(): Promise<any> {
    return Swal.fire({
      ...this.baseConfig,
      icon: 'error',
      title: 'Error',
      text: 'An unexpected error occurred while deleting the session. Please try again.',
      confirmButtonText: 'OK'
    });
  }

  // ============================================================
  // USER VALIDATION & API ERRORS
  // ============================================================

  showUserIdNotFound(context: 'save' | 'delete'): Promise<any> {
    const message = context === 'save'
      ? 'User ID not found. Unable to save chat session.'
      : 'Cannot delete session: User ID not found.';

    return Swal.fire({
      ...this.baseConfig,
      icon: 'error',
      text: message,
      confirmButtonText: 'OK'
    });
  }

  showSessionSaveError(): Promise<any> {
    return Swal.fire({
      ...this.baseConfig,
      icon: 'error',
      text: 'Error saving chat session. Please try again. If the problem persists, the server might be experiencing issues.',
      confirmButtonText: 'OK'
    });
  }

  showSessionDeleteApiError(): Promise<any> {
    return Swal.fire({
      ...this.baseConfig,
      icon: 'error',
      text: 'Error deleting session on server. Please try again.',
      confirmButtonText: 'OK'
    });
  }

  // ============================================================
  // MODEL CHANGES
  // ============================================================

  showModelSwitchConfirmation(modelName: string, isGPT5Mini: boolean, hasExistingChat: boolean): Promise<any> {
    let additionalInfo = '';

    if (hasExistingChat) {
      additionalInfo = 'Changing inputs will start a new chat session.<br>';
    }

    if (isGPT5Mini) {
      additionalInfo += 'GPT-5 Mini uses reasoning, which means responses may take longer than other chat models.';
    } else if (hasExistingChat) {
      additionalInfo = 'Changing inputs will start a new chat session.';
    }

    let htmlContent = `
      <p style="color: ${this.COLORS.textSecondary}; line-height: 1.55;">
        Would you like to switch the model to:<br>
        '<strong style="color: ${this.COLORS.text};">${modelName.replace(/-/g, ' ')}</strong>'?
      </p>
    `;

    if (additionalInfo) {
      htmlContent += `
        <div style="
          margin-top: 14px;
          padding: 12px 14px;
          background: #FEFCE8;
          border-radius: 10px;
          text-align: left;
          font-size: 0.88em;
        ">
          <span style="color: #854D0E; line-height: 1.5;">
            ${additionalInfo}
          </span>
        </div>
      `;
    }

    return Swal.fire({
      ...this.primaryConfig,
      title: 'Switch Model',
      html: htmlContent,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Switch Model',
      cancelButtonText: 'Cancel',
      customClass: {
        ...this.primaryConfig.customClass as Record<string, string>,
        cancelButton: 'swal-clean-cancel'
      }
    });
  }

  // ============================================================
  // VOICE INPUT
  // ============================================================

  showVoiceNotSupported(): Promise<any> {
    return Swal.fire({
      ...this.baseConfig,
      icon: 'error',
      title: 'Not Supported',
      text: 'Voice input is not supported in your browser. Please use Chrome, Edge, or Safari.',
      confirmButtonText: 'OK'
    });
  }

  showVoiceRecognitionError(errorType: string): Promise<any> {
    const errorMessages: { [key: string]: string } = {
      'no-speech': 'No speech was detected. Please try again.',
      'audio-capture': 'No microphone was found. Please ensure your microphone is connected.',
      'not-allowed': 'Permission to use microphone was denied. Please allow microphone access.',
      'network': 'Network error occurred. Please check your internet connection.'
    };

    const message = errorMessages[errorType] || 'An error occurred during voice recognition.';

    return Swal.fire({
      ...this.baseConfig,
      icon: 'error',
      title: 'Voice Recognition Error',
      text: message,
      confirmButtonText: 'OK'
    });
  }

  // ============================================================
  // FEEDBACK
  // ============================================================

  showFeedbackMissing(): Promise<any> {
    return Swal.fire({
      ...this.baseConfig,
      icon: 'warning',
      title: 'Missing Information',
      text: 'Please enter your feedback.',
      timer: 2000,
      showConfirmButton: false
    });
  }

  showFeedbackSuccess(): Promise<any> {
    return Swal.fire({
      icon: 'success',
      title: 'Thank you!',
      text: 'Your feedback has been submitted successfully.',
      toast: true,
      position: 'top',
      timer: 2500,
      showConfirmButton: false,
      customClass: {
        popup: 'swal-clean-toast'
      }
    });
  }

  showFeedbackError(): Promise<any> {
    return Swal.fire({
      ...this.baseConfig,
      icon: 'error',
      title: 'Failed',
      text: 'Failed to send feedback. Please try again later.',
      confirmButtonText: 'OK'
    });
  }

  // ============================================================
  // IMAGE GENERATION
  // ============================================================

  showImageGenerationError(errorMessage?: string): Promise<any> {
    return Swal.fire({
      ...this.baseConfig,
      icon: 'error',
      title: 'Image Generation Failed',
      text: errorMessage || 'Unable to generate image. Please try again.',
      confirmButtonText: 'OK'
    });
  }

  // ============================================================
  // ADDITIONAL FILE VALIDATION
  // ============================================================

  showInvalidFilesWithList(invalidFiles: string[], model: string, maxSizeMB: number): Promise<any> {
    const fileList = invalidFiles.map((f: string) => `<li style="text-align:left;">${f}</li>`).join('');
    return Swal.fire({
      ...this.baseConfig,
      icon: 'warning',
      title: 'Invalid Files',
      html: `
        <div style="margin-bottom: 14px;">
          <p style="color: ${this.COLORS.textSecondary};">The following files don't meet the requirements for:<br><strong style="color: ${this.COLORS.text};">${model.replace(/-/g, ' ').toUpperCase()}</strong></p>
          <ul style="padding-left: 20px; margin-top: 10px; color: ${this.COLORS.danger}; font-size: 0.88em; line-height: 1.6;">
            ${fileList}
          </ul>
        </div>
        <div style="
          font-size: 0.85em;
          color: ${this.COLORS.textSecondary};
          background: ${this.COLORS.surface};
          padding: 12px 14px;
          border-radius: 10px;
          border: 1px solid ${this.COLORS.border};
          text-align: left;
          line-height: 1.6;
        ">
          <strong style="color: ${this.COLORS.text};">Max file size:</strong> ${maxSizeMB} MB<br>
          <strong style="color: ${this.COLORS.text};">Allowed types:</strong> PDF, Images (PNG, JPG, JPEG, WebP, GIF), Text files, DOCX, XLSX, CSV
        </div>
      `,
      confirmButtonText: 'OK'
    });
  }

  showCombinedFileSizeExceeded(model: string, maxSizeMB: number): Promise<any> {
    return Swal.fire({
      ...this.baseConfig,
      icon: 'warning',
      title: 'File Size Limit Exceeded',
      html: `
        <div style="color: ${this.COLORS.textSecondary}; line-height: 1.55;">
          Combined file size exceeds the limits for:<br>
          <strong style="color: ${this.COLORS.text};">${model.replace(/-/g, ' ').toUpperCase()}</strong>
          <br><br><strong style="color: ${this.COLORS.text};">Allowed:</strong> ${maxSizeMB} MB
        </div>
      `,
      confirmButtonText: 'OK'
    });
  }

  // ============================================================
  // MODEL CHANGE WITH GPT-5 MINI
  // ============================================================

  showGPT5MiniInfo(modelName: string): Promise<any> {
    return Swal.fire({
      ...this.primaryConfig,
      title: 'Switch Model',
      icon: 'question',
      html: `
        <p style="color: ${this.COLORS.textSecondary}; line-height: 1.55;">
          Would you like to switch the model to:<br>
          '<strong style="color: ${this.COLORS.text};">${modelName.replace(/-/g, ' ')}</strong>'?
        </p>
        <div style="
          margin-top: 14px;
          padding: 12px 14px;
          background: #FEFCE8;
          border-radius: 10px;
          text-align: left;
          font-size: 0.88em;
        ">
          <span style="color: #854D0E; line-height: 1.5;">
            Changing inputs will start a new chat session.<br>
            GPT-5 Mini uses reasoning, which means responses may take longer than other chat models.
          </span>
        </div>
      `,
      confirmButtonText: 'OK'
    });
  }

  // ============================================================
  // GENERIC UTILITIES
  // ============================================================

  showSuccess(title: string, message: string): Promise<any> {
    return Swal.fire({
      ...this.baseConfig,
      icon: 'success',
      title: title,
      text: message,
      timer: 2500,
      showConfirmButton: false
    });
  }

  showError(message: string, title: string = 'Error'): Promise<any> {
    return Swal.fire({
      ...this.baseConfig,
      icon: 'error',
      title: title,
      text: message,
      confirmButtonText: 'OK'
    });
  }
}